import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Send, Smartphone, Users } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface NotificationRow {
  readonly id: string;
  readonly userId: string;
  readonly title: string;
  readonly body: string;
  readonly type: string;
  readonly isRead: boolean | null;
  readonly articleId: string | null;
  readonly articleTitle: string | null;
  readonly createdAt: string | null;
}

interface Response {
  readonly kpis: {
    readonly sent24h: number;
    readonly sentToday: number;
    readonly activeDevices: number;
    readonly platformMix: { readonly ios: number; readonly android: number };
    readonly usersWithPushEnabled: number;
  };
  readonly recent: readonly NotificationRow[];
  readonly hourly: ReadonlyArray<{ readonly hour: string; readonly count: number }>;
}

const fmtInt = new Intl.NumberFormat('en-US');

const columns: readonly Column<NotificationRow>[] = [
  {
    key: 'createdAt',
    header: 'When',
    width: '160px',
    render: (r) =>
      r.createdAt ? (
        <span title={r.createdAt} style={{ color: 'var(--text-secondary)' }}>
          {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
        </span>
      ) : (
        '—'
      ),
  },
  {
    key: 'type',
    header: 'Type',
    width: '110px',
    render: (r) => <span className="nw-chip nw-chip--info">{r.type}</span>,
  },
  {
    key: 'title',
    header: 'Title',
    render: (r) => <span style={{ fontWeight: 500 }}>{r.title}</span>,
  },
  {
    key: 'articleTitle',
    header: 'Article',
    render: (r) =>
      r.articleTitle ? (
        <span style={{ color: 'var(--text-secondary)' }}>{r.articleTitle}</span>
      ) : (
        '—'
      ),
  },
  {
    key: 'isRead',
    header: 'Read',
    width: '80px',
    render: (r) =>
      r.isRead ? (
        <span className="nw-chip nw-chip--success">yes</span>
      ) : (
        <span className="nw-chip">no</span>
      ),
  },
];

function Kpi({
  label,
  value,
  Icon,
  detail,
}: {
  readonly label: string;
  readonly value: number;
  readonly Icon: typeof Bell;
  readonly detail?: string;
}) {
  return (
    <div className="nw-kpi">
      <div className="nw-kpi__head">
        <span className="nw-kpi__icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="nw-kpi__label">{label}</span>
      </div>
      <div className="nw-kpi__value">{fmtInt.format(value)}</div>
      {detail && <span className="nw-kpi__delta-note">{detail}</span>}
    </div>
  );
}

export function Notifications() {
  const query = useQuery({
    queryKey: queryKeys.notifications(),
    queryFn: async () => {
      const res = await apiClient.get<Response>('/owner/notifications');
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Notifications</h1>
        <p className="nw-page-header__subtitle">
          Push delivery, device platform mix, and opt-in rate. Refreshes every minute.
        </p>
      </header>

      {data && (
        <div className="nw-kpi-grid">
          <Kpi label="Sent 24h" value={data.kpis.sent24h} Icon={Send} />
          <Kpi label="Sent today" value={data.kpis.sentToday} Icon={Bell} />
          <Kpi
            label="Active devices"
            value={data.kpis.activeDevices}
            Icon={Smartphone}
            detail={`iOS ${fmtInt.format(data.kpis.platformMix.ios)} · Android ${fmtInt.format(data.kpis.platformMix.android)}`}
          />
          <Kpi
            label="Push opt-in"
            value={data.kpis.usersWithPushEnabled}
            Icon={Users}
          />
        </div>
      )}

      <section className="nw-panel">
        <header className="nw-panel__header">
          <h2 className="nw-panel__title">Recent</h2>
        </header>
        <DataTable
          columns={columns}
          rows={data?.recent ?? []}
          rowKey={(r) => r.id}
          loading={query.isLoading}
          error={query.error ? (query.error as Error).message : null}
          emptyMessage="No notifications sent yet."
        />
      </section>
    </>
  );
}
